import { TierBadge } from '@components/ui/TierBadge';
import {
  type GatedFeature,
  getRequiredTier,
  visibleAnalysisModes,
} from '@parlay-picker/shared/featureAccess';
import {
  getQuotaForMode,
  isModeAllowed,
  TIER_DISPLAY_NAMES,
} from '@parlay-picker/shared/subscriptionTier';
import { getIncludedTiers, TIER_CRITERIA } from '@parlay-picker/shared/tierCriteria';
import { useQuotaUsage } from '@stores/generationStore';
import type { AnalysisConfig, AnalysisMode } from '@views/picks/constants';
import type React from 'react';
import type { ReactElement } from 'react';
import { MODE_OPTIONS, TIER_COLORS } from './configConstants';
import type { ExpandedSections } from './configTypes';
import { Section } from './Section';

export function AnalysisModeSelector({
  config,
  setConfig,
  expandedSections,
  toggleSection,
  onUpgrade,
}: {
  config: AnalysisConfig;
  setConfig: React.Dispatch<React.SetStateAction<AnalysisConfig>>;
  expandedSections: ExpandedSections;
  toggleSection: (key: string) => void;
  onUpgrade: (feature: GatedFeature) => void;
}): ReactElement {
  const quotaUsage = useQuotaUsage();
  const tier = quotaUsage?.tier ?? 'free';

  const shownModes = visibleAnalysisModes(tier);
  const visibleModes = MODE_OPTIONS.filter((opt) => shownModes.includes(opt.value));

  return (
    <Section
      title="Analysis Mode"
      sectionKey="outputOptions"
      isExpanded={expandedSections.outputOptions}
      onToggleExpand={toggleSection}
      enabledCount={0}
      totalCount={0}
      showCount={false}
    >
      <div data-testid="config-mode-selector" className="space-y-2">
        {visibleModes.map((option) => {
          const isSelected = (config.outputOptions?.analysisMode ?? 'standard') === option.value;
          const allowed = isModeAllowed(tier, option.value);
          const modeQuota = getQuotaForMode(quotaUsage, option.value);
          const exhausted = allowed && modeQuota !== null && modeQuota.remaining <= 0;
          const disabled = !allowed || exhausted;

          return (
            <button
              type="button"
              key={option.value}
              onClick={() => {
                if (!allowed) {
                  onUpgrade('analysis_detailed');
                  return;
                }
                if (exhausted) return;
                setConfig((prev) => ({
                  ...prev,
                  outputOptions: {
                    ...prev.outputOptions,
                    analysisMode: option.value as AnalysisMode,
                  },
                }));
              }}
              className={`w-full text-left px-3 py-2.5 rounded-lg border transition-all ${
                exhausted
                  ? 'border-theme-border bg-theme-surface-hover opacity-60 cursor-not-allowed'
                  : !allowed
                    ? 'border-theme-border bg-theme-surface-hover cursor-pointer hover:border-theme-text-tertiary'
                    : isSelected
                      ? 'border-purple-500 bg-purple-900/30 ring-1 ring-purple-500/30'
                      : 'border-theme-border bg-theme-surface-hover hover:border-theme-text-tertiary'
              }`}
            >
              <div className="flex items-center justify-between">
                <span
                  className={`text-sm font-medium ${
                    disabled
                      ? 'text-theme-text-tertiary'
                      : isSelected
                        ? 'text-purple-300'
                        : 'text-theme-text-secondary'
                  }`}
                >
                  {option.label}
                </span>
                <span className="flex items-center gap-2">
                  {!allowed && <TierBadge feature="analysis_detailed" size="md" />}
                  {exhausted && <span className="text-xs text-theme-warning">0 remaining</span>}
                  {allowed && !exhausted && modeQuota && (
                    <span className="text-xs text-theme-text-tertiary">
                      {modeQuota.remaining}/{modeQuota.limit}
                    </span>
                  )}
                  {isSelected && !disabled && (
                    <span className="text-xs text-purple-400">Active</span>
                  )}
                </span>
              </div>
              <span className="text-xs text-theme-text-tertiary">{option.desc}</span>
            </button>
          );
        })}
      </div>
    </Section>
  );
}

export function TierDisplay({
  minLegs,
  maxLegs,
  minOdds,
  maxOdds,
  lotteryLocked,
}: {
  minLegs: number;
  maxLegs: number;
  minOdds: number;
  maxOdds: number;
  lotteryLocked: boolean;
}): ReactElement {
  const includedTiers = getIncludedTiers(minLegs, maxLegs, minOdds, maxOdds);
  const includedKeys = new Set(includedTiers.map((t) => t.key));

  return (
    <div>
      <div className="flex items-center justify-between mb-2">
        <span className="text-sm text-white">Included Tiers</span>
        <span className="text-xs text-theme-text-tertiary">
          {includedTiers.filter((t) => !t.proOnly || !lotteryLocked).length} active
        </span>
      </div>
      <div className="flex flex-wrap gap-1.5">
        {TIER_CRITERIA.map((tier) => {
          const isIncluded = includedKeys.has(tier.key);
          const isProLocked = tier.proOnly && lotteryLocked;
          const isActive = isIncluded && !isProLocked;
          const colors = TIER_COLORS[tier.key] || TIER_COLORS.safe;

          return (
            <div
              key={tier.key}
              className={`
                px-2.5 py-1 rounded-md border text-xs font-medium transition-all duration-200
                ${isActive ? colors.active : colors.dim}
                ${isActive ? colors.text : 'text-gray-600'}
              `}
              title={
                isProLocked
                  ? `${tier.name}: needs the ${TIER_DISPLAY_NAMES[getRequiredTier('lottery_ticket_tier')]} plan`
                  : `${tier.name}: ${tier.minLegs}-${tier.maxLegs} legs, +${tier.minOdds}${tier.maxOdds === Infinity ? '+' : `-${tier.maxOdds}`} odds`
              }
            >
              {tier.name}
              {isProLocked && isIncluded && (
                <span className="ml-1">
                  <TierBadge feature="lottery_ticket_tier" />
                </span>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
